import type { FavoriteItem } from '../store/favoritesStore';

/**
 * 즐겨찾기 갈래 나누기 (2026-09-04) — 신규 파일.
 *
 * 좌측 [즐겨찾기]를 펼치면 스페이스도 프로젝트도 레일 메뉴도 한 줄로 섞여 나왔다.
 * 이름만으로는 '승인 흐름' 이 스페이스인지 프로젝트인지 알 수 없어서, 담아 둔 사람이
 * 자기가 뭘 담았는지 목록을 눌러 봐야 알았다.
 *
 * **저장 형식은 건드리지 않는다.** favoritesStore 는 경로(`to`) 하나로 즐겨찾기를 식별하는데,
 * 그 경로가 이미 갈래를 말하고 있다 — 등록하는 쪽(FavoriteStar)이 화면마다 다른 모양의
 * 경로를 담기 때문이다:
 *
 *   스페이스   `/project/space/{teamId}`            ProjectHomeSpaces · SpaceDetailPage
 *   프로젝트   `/task?project={id}`                 ProjectHomeProjects (스페이스 소속이면 team= 이 함께 붙는다)
 *   메뉴       `/chat` · `/mail` · 외부 URL …       우측 레일 팝오버에서 담은 1차 메뉴
 *
 * 그래서 store 에 컬럼을 더하거나 이미 담긴 항목을 옮길 필요가 없다(마이그레이션 0).
 * 등록 경로가 바뀌면 여기만 고치면 된다.
 */

export type FavoriteKind = 'space' | 'project' | 'menu';

/** 스페이스 — `/project/space/{id}`. 뒤에 쿼리·해시가 붙어도 같은 갈래다. */
const SPACE_RE = /^\/project\/space\/[^/?#]+/;

/**
 * 프로젝트 — `/task` 로 가면서 `project=` 를 들고 있는 것.
 *
 * ⚠ `team=` 만 있고 `project=` 가 없는 `/task?team=X` 는 프로젝트가 아니다(스페이스의 보드).
 *   그래서 경로만 보지 않고 쿼리에 project 가 실제로 있는지까지 본다.
 */
function isProjectPath(to: string): boolean {
  const q = to.indexOf('?');
  if (q < 0) return false;
  if (!/^\/task(\/|$|\?)/.test(to.slice(0, q) + '?')) return false;
  return new URLSearchParams(to.slice(q + 1)).has('project');
}

/**
 * 즐겨찾기 한 건의 갈래.
 *
 * 외부 링크는 경로 모양과 무관하게 늘 '메뉴' 다 — 우리 라우트가 아니므로 스페이스·프로젝트일 수 없다.
 * 알 수 없는 내부 경로도 '메뉴' 로 떨어뜨린다(빈 칸으로 사라지는 것보다 낫다).
 */
export function favoriteKind(item: Pick<FavoriteItem, 'to' | 'external'>): FavoriteKind {
  if (item.external) return 'menu';
  const to = item.to ?? '';
  if (SPACE_RE.test(to)) return 'space';
  if (isProjectPath(to)) return 'project';
  return 'menu';
}

/** 화면에 세우는 순서 — 큰 것(스페이스)에서 작은 것(메뉴)으로. */
export const FAVORITE_KIND_ORDER: readonly FavoriteKind[] = ['space', 'project', 'menu'];

export interface FavoriteGroup {
  kind: FavoriteKind;
  items: FavoriteItem[];
}

/**
 * 갈래별로 묶는다. **비어 있는 갈래는 아예 돌려주지 않는다** — 담은 적 없는 갈래의
 * 머리글이 서 있으면, 목록이 짧을 때 머리글이 항목보다 많아진다.
 *
 * 갈래 안의 순서는 store 에 담긴 순서(등록 순) 그대로다 — 여기서 다시 정렬하면
 * 우측 레일 팝오버와 순서가 갈린다.
 */
export function groupFavorites(items: readonly FavoriteItem[]): FavoriteGroup[] {
  const buckets = new Map<FavoriteKind, FavoriteItem[]>();
  for (const it of items) {
    const k = favoriteKind(it);
    const cur = buckets.get(k);
    if (cur) cur.push(it);
    else buckets.set(k, [it]);
  }
  return FAVORITE_KIND_ORDER
    .filter((k) => (buckets.get(k)?.length ?? 0) > 0)
    .map((k) => ({ kind: k, items: buckets.get(k)! }));
}
